<template>
  <Form @submit.prevent="submit" class="card p-2 sm:p-4">
    <IftaLabel class="mb-3 w-full">
      <InputText maxlength="100" id="txt_title" v-model="form.workshopTitle"
        class="w-full h-10 sm:h-12 text-sm sm:text-base"/>
      <label for="txt_title" class="text-base sm:text-lg">{{ $t('WorkshopFormComponent.title') }}</label>
    </IftaLabel>
    <IftaLabel class="mb-3 w-full">
      <InputText maxlength="100" id="txt_description" v-model="form.description"
        class="w-full h-10 sm:h-12 text-sm sm:text-base"/>
      <label for="txt_description" class="text-base sm:text-lg">{{ $t('WorkshopFormComponent.description') }}</label>
    </IftaLabel>
    <label class="text-[var(--color-disabled)] text-sm sm:text-base">{{ $t('WorkshopFormComponent.beginDate') }}</label>
    <date-time-selection-component v-model="form.dateWorkshopBegin" isStartTime="true"
      :hideRecurring="true"/>
    <label class="text-[var(--color-disabled)] text-sm sm:text-base mt-2">{{ $t('WorkshopFormComponent.endDate') }}</label>
    <date-time-selection-component v-model="form.dateWorkshopEnd" isStartTime="false"
      :hideRecurring="true"/>
    <MultiSelect v-model="form.projectName" display="chip" :options="projectName" optionLabel="name"
      filter :placeholder="$t('WorkshopFormComponent.addProjectName')"
      class="w-full mt-3 text-sm sm:text-base" />
    <MultiSelect v-model="form.participants" display="chip" :options="participants"
      optionLabel="name" filter :placeholder="$t('WorkshopFormComponent.addParticipants')"
      class="w-full mt-3 text-sm sm:text-base" />
    <Select v-model="form.tags" :options="workshopTags" optionLabel="value" option-value="key"
      :placeholder="$t('WorkshopFormComponent.tagDropdown')" class="w-full mt-3 text-sm sm:text-base" />
    <Select v-model="form.workshopFacilitator" :options="participants" optionLabel="name" filter
      :placeholder="$t('WorkshopFormComponent.addFacilitator')" class="w-full mt-3 text-sm sm:text-base" />
    <div class="flex flex-col-reverse sm:flex-row justify-end gap-2 mt-6 sm:mt-8 mb-4">
      <Button
            v-if="isEdit"
            type="button"
            class="text-red-600 bg-none rounded-full w-fit min-h-10 px-4 py-2 text-sm sm:text-base hover:text-red-700"
            :label="$t('WorkshopComponent.deleteWorkshopButton')"
            icon="pi pi-trash"
            @click.stop="deleteWorkshop"
            unstyled
          />
      <Button type="submit" v-if="!isEdit" @click.prevent="submit"
        class="btn-primary-accent w-full sm:w-auto" unstyled>
        {{ $t('WorkshopFormComponent.create') }}
      </Button>
      <Button type="submit" v-else @click.prevent="update"
        class="btn-primary-accent w-full sm:w-auto" unstyled>
        {{ $t('WorkshopFormComponent.edit') }}
      </Button>
    </div>
  </Form>
</template>

<script>
import NetworkContent from '@/helpers/networking/NetworkContent';
import DateTimeSelectionComponent from './DateTimeSelectionComponent.vue';
import { Form } from '@primevue/forms';
import {
  Button,
  IftaLabel,
  InputText,
  MultiSelect,
  Select,
} from 'primevue';
import { getFeatureDisplayNameOrFallback } from '@/utils/featureDisplay';

export default {
  name: 'WorkshopFormComponent',
  props: {
    value: Object,
  },
  emits: ['submit'],
  components: {
    DateTimeSelectionComponent,
    Form,
    IftaLabel,
    InputText,
    MultiSelect,
    Select,
    Button,
  },
  data() {
    return {
      form: {
        tags: '',
        participants: [],
        workshopFacilitator: {},
        dateWorkshopBegin: new Date(),
        dateWorkshopEnd: new Date(),
        ...this.value,
      },
      projectNames: [],
      participants: [],
      deleteButtonText: this.$t('KssTaskCreatedComponent.popup.deleteButton'),
      cancelButtonText: this.$t('KssTaskCreatedComponent.popup.cancelButton'),
    };
  },
  computed: {
    featureName() {
      return getFeatureDisplayNameOrFallback(
        this.$store,
        'workshops',
        this.$t('FallbackTexts.workshop'),
        this.$i18n?.locale,
      );
    },
    isEdit() {
      return !!this.value?._id;
    },
    workshopTags() {
      const workshopTags = [];
      Object.entries(this.$store.getters.getWorkshopTags).map((ele) => {
        workshopTags.push({ key: ele[0], value: ele[1] });
        return ele;
      });
      return workshopTags;
    },
    popupMessage() {
      console.log(this.featureName);
      return this.$t('ToastComponent.workshop.workshopDeletedConfirm', { text: this.featureName });
    },
  },
  methods: {
    async submit() {
      if (this.checkForErrors()) {
        return;
      }
      const params = {
        workshopTitle: this.form.workshopTitle,
        tags: this.form.tags,
        description: this.form.description,
        projectName: this.form.projectName,
        participants: this.form.participants.map((s) => s._id),
        workshopFacilitatorID: this.form.workshopFacilitator._id,
      };
      params.dateWorkshopBegin = new Date(this.form.dateWorkshopBegin).toUTCString();
      params.dateWorkshopEnd = new Date(this.form.dateWorkshopEnd).toUTCString();
      if (this.form._id) {
        params._id = this.form._id;
      }

      try {
        await this.$store.dispatch('createWorkshop', params);
        this.$toast.add({
          severity: 'success',
          summary: this.$t('ToastComponent.workshop.workshopCreated', { text: this.featureName }),
          detail: '',
          life: 3000,
        });
        this.closePopup();
      } catch (error) {
        this.$toast.add({
          severity: 'error',
          summary: error?.response?.data?.message,
          detail: '',
          life: 3000,
        });
      }
    },
    async update() {
      if (this.checkForErrors()) {
        return;
      }
      const params = {
        workshopTitle: this.form.workshopTitle,
        tags: this.form.tags,
        description: this.form.description,
        projectName: this.form.projectName,
        participants: this.form.participants.map((s) => s._id),
        workshopFacilitatorID: this.form.workshopFacilitator._id,
      };
      params.dateWorkshopBegin = new Date(this.form.dateWorkshopBegin).toUTCString();
      params.dateWorkshopEnd = new Date(this.form.dateWorkshopEnd).toUTCString();
      if (this.form._id) {
        params._id = this.form._id;
      }

      try {
        await this.$store.dispatch('updateWorkshop', params);
        this.$toast.add({
          severity: 'success',
          summary: this.$t('ToastComponent.workshop.workshopUpdated', { text: this.featureName }),
          detail: '',
          life: 3000,
        });
        this.$emit('hide-details');
      } catch (error) {
        this.$toast.add({
          severity: 'error',
          summary: error?.response?.data?.message,
          detail: '',
          life: 3000,
        });
      }
    },
    async closePopup() {
      await this.$nextTick();
      // this.$store.commit('removeFrontendOverlay', this.data.id);
      this.$emit('close-overlay');
    },
    checkForErrors() {
      let hasError = false;
      const beginDate = new Date(this.form.dateWorkshopBegin);
      const endDate = new Date(this.form.dateWorkshopEnd);
      if (beginDate >= endDate) {
        this.$toast.add({
          severity: 'error',
          summary: this.$t('WorkshopFormComponent.theBeginDateMustBeEarlier'),
          detail: '',
          life: 3000,
        });
        hasError = true;
      }

      if (!this.form.workshopTitle) {
        this.$toast.add({
          severity: 'error',
          summary: this.$t('WorkshopFormComponent.titleRequired'),
          detail: '',
          life: 3000,
        });
        hasError = true;
      }

      if (this.form.participants.length === 0) {
        this.$toast.add({
          severity: 'error',
          summary: this.$t('WorkshopFormComponent.selectParticipantsErr'),
          detail: '',
          life: 3000,
        });
        hasError = true;
      }

      if (this.form.tags === '') {
        this.$toast.add({
          severity: 'error',
          summary: this.$t('WorkshopFormComponent.selectTag'),
          detail: '',
          life: 3000,
        });
        hasError = true;
      }

      return hasError;
    },
    async getParticipants() {
      const { data } = await NetworkContent.getParticipants();
      const participants = data.users ?? [];
      this.participants = participants.map((s) => ({
        name: s.name,
        _id: s.id,
      }));
    },
    async getProjectNames() {
      const { data } = await NetworkContent.getProjectNames();
      this.projectNames = data.content ?? [];
    },
    async getWorkshopTags() {
      await this.$store.dispatch('getWorkshopTags');
    },
    deleteWorkshop() {
      this.showPopup = true;
      this.$confirm.require({
        message: this.popupMessage,
        header: 'Confirmation',
        icon: 'pi pi-exclamation-triangle',
        rejectProps: {
          label: this.cancelButtonText,
          severity: 'secondary',
          outlined: true,
        },
        acceptProps: {
          label: this.deleteButtonText,
        },
        accept: this.onPopupConfirm,
        reject: this.onPopupCancel,
      });
    },
    onPopupCancel() {
      this.showPopup = false;
    },
    async onPopupConfirm() {
      try {
        this.$store.dispatch('deleteWorkshop', this.form._id);
        this.showPopup = false;
        this.$toast.add({
          severity: 'success',
          summary: this.$t('ToastComponent.workshop.workshopDeleted', { text: this.featureName }),
          detail: '',
          life: 3000,
        });
      } catch (error) {
        this.$toast.add({
          severity: 'error',
          summary: this.$t('ToastComponent.workshop.workshopDeletedError', { text: this.featureName }),
          detail: '',
          life: 3000,
        });
      }
    },
  },
  mounted() {
    this.getParticipants();
    this.getProjectNames();
    this.getWorkshopTags();
  },
};
</script>

<style scoped>
.card {
    border: none !important;
    background-color: transparent;
}
</style>
